/**
 * DeductionDetailPage Component
 * 일차감 상세 페이지 - VROONG 브랜드 통일 디자인
 */

import React from 'react';
import BottomNavigation from '../../../core/components/BottomNavigation';
import { useDeductionDetail } from '../hooks/useDeductionDetail';
import styles from '../styles/DeductionDetailPage.module.css';

const DeductionDetailPage: React.FC = () => {
    const {
        item,
        dailyBreakdown,
        getStatusLabel,
        getCalcTypeLabel,
        formatAmount,
        handleBack,
        isLoading,
        error,
    } = useDeductionDetail();

    // 로딩 상태
    if (isLoading && !item) {
        return (
            <div className={`screen active ${styles['detail-screen']}`}>
                <div className={styles['detail-header']}>
                    <div className={styles['header-top']}>
                        <button className={styles['back-btn']} onClick={handleBack}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h1 className={styles['page-title']}>일차감 상세</h1>
                    </div>
                </div>
                <div className={styles['detail-container']}>
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>상세 정보를 불러오는 중...</p>
                    </div>
                </div>
                <BottomNavigation currentScreen="deduction" />
            </div>
        );
    }

    // 에러 또는 데이터 없음 상태
    if (!item) {
        return (
            <div className={`screen active ${styles['detail-screen']}`}>
                <div className={styles['detail-header']}>
                    <div className={styles['header-top']}>
                        <button className={styles['back-btn']} onClick={handleBack}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h1 className={styles['page-title']}>일차감 상세</h1>
                    </div>
                </div>
                <div className={styles['detail-container']}>
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>{error ?? '상세 정보를 불러오지 못했습니다.'}</p>
                        <button className={styles['back-button']} onClick={handleBack}>
                            목록으로 돌아가기
                        </button>
                    </div>
                </div>
                <BottomNavigation currentScreen="deduction" />
            </div>
        );
    }

    const isFixedCalc = item.calcType === '1' || item.calcType?.toUpperCase() === 'FIXED';
    const isRateCalc = item.calcType === '2' || item.calcType?.toUpperCase() === 'RATE';

    const totalAmount = dailyBreakdown.reduce((sum, day) => {
        if (typeof day.amount === 'number') {
            return sum + day.amount;
        }
        return sum;
    }, 0);

    const totalRate = dailyBreakdown.reduce((sum, day) => {
        if (typeof day.rate === 'number') {
            return sum + day.rate;
        }
        return sum;
    }, 0);

    const hasAmountTotal = isFixedCalc && dailyBreakdown.some((day) => day.amount !== null && !Number.isNaN(day.amount));
    const hasRateTotal = isRateCalc && dailyBreakdown.some((day) => day.rate !== null && !Number.isNaN(day.rate));

    const isActive = item.terminatedYn === 'N';

    return (
        <div className={`screen active ${styles['detail-screen']}`}>
            {/* 헤더 */}
            <div className={styles['detail-header']}>
                <div className={styles['header-top']}>
                    <button className={styles['back-btn']} onClick={handleBack}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className={styles['page-title']}>일차감 상세</h1>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['detail-container']}>
                {isLoading && (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>상세 정보를 불러오는 중...</p>
                    </div>
                )}

                {error && (
                    <div className={styles['error-section']}>
                        <p className={styles['error-text']}>{error}</p>
                    </div>
                )}

                {/* 타이틀 카드 */}
                <div className={styles['detail-card']}>
                    <div className={styles['title-row']}>
                        <h2 className={styles['deduction-title']}>{item.deductName}</h2>
                        <span className={`${styles['status-badge']} ${isActive ? styles['active'] : styles['expired']}`}>
                            <span className={styles['status-icon']}>{isActive ? '✅' : '⏸️'}</span>
                            {getStatusLabel(item.terminatedYn || 'N')}
                        </span>
                    </div>

                    <div className={styles['divider']}></div>

                    {/* 공제 정보 섹션 */}
                    <div className={styles['section']}>
                        <h3 className={styles['section-title']}>
                            <span className={styles['section-title-icon']}>💰</span>
                            공제 정보
                        </h3>
                        <div className={styles['info-grid']}>
                            <div className={styles['info-item']}>
                                <div className={styles['info-label']}>공제 방식</div>
                                <div className={styles['info-value']}>{getCalcTypeLabel(item.calcType || '1')}</div>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-label']}>공제 금액/율</div>
                                <div className={`${styles['info-value']} ${styles['highlight']}`}>
                                    {item.calcType === '1'
                                        ? formatAmount(item.deductAmt)
                                        : item.deductRate !== null
                                            ? `${item.deductRate}%`
                                            : '-'}
                                </div>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-label']}>시작 일자</div>
                                <div className={styles['info-value']}>{item.startDay || '-'}</div>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-label']}>종료 일자</div>
                                <div className={styles['info-value']}>{item.endDay || '-'}</div>
                            </div>
                            {item.tranDay && (
                                <div className={styles['info-item']}>
                                    <div className={styles['info-label']}>집계 일자</div>
                                    <div className={styles['info-value']}>{item.tranDay}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles['divider']}></div>

                    {/* 일별 공제 내역 섹션 */}
                    <div className={styles['section']}>
                        <h3 className={styles['section-title']}>
                            <span className={styles['section-title-icon']}>📋</span>
                            일별 공제 내역
                        </h3>
                        {dailyBreakdown.length === 0 ? (
                            <p className={styles['empty-breakdown']}>조회된 공제 데이터가 없습니다.</p>
                        ) : (
                            <div className={styles['breakdown-table-wrapper']}>
                                <table className={styles['breakdown-table']}>
                                    <thead>
                                        <tr>
                                            <th>일자</th>
                                            <th>{isFixedCalc ? '일별 공제액' : '일별 공제율'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dailyBreakdown.map((day) => (
                                            <tr key={day.label}>
                                                <td>{day.label}</td>
                                                <td>
                                                    {isFixedCalc && day.amount !== null
                                                        ? `${Math.round(day.amount).toLocaleString()} 원`
                                                        : isRateCalc && day.rate !== null
                                                            ? `${day.rate}%`
                                                            : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {(hasAmountTotal || hasRateTotal) && (
                                        <tfoot>
                                            <tr>
                                                <td>합계</td>
                                                <td>
                                                    {hasAmountTotal
                                                        ? `${Math.round(totalAmount).toLocaleString()} 원`
                                                        : hasRateTotal
                                                            ? `${totalRate}%`
                                                            : '-'}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="deduction" />
        </div>
    );
};

export default DeductionDetailPage;
